import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  readStoredDriverLang,
  translateDriverString,
  translateMapBuffer,
  translateDriverError,
  formatDriverDeliverySlot,
  formatDriverDeliveryDate,
  formatDriverWeight,
  applyDriverDocumentTitle,
  DRIVER_LANG_STORAGE_KEY,
} from "@/lib/driverI18n";
import { formatDriverAddress } from "@/lib/driverAddressHindi";

const DriverLanguageContext = createContext(null);

export function DriverLanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredDriverLang);

  const setLang = useCallback((next) => {
    const v = next === "hi" ? "hi" : "en";
    setLangState(v);
    try {
      localStorage.setItem(DRIVER_LANG_STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === "hi" ? "en" : "hi");
  }, [lang, setLang]);

  const t = useCallback(
    (key, params) => translateDriverString(lang, key, params),
    [lang]
  );

  const tError = useCallback(
    (message) => translateDriverError(lang, message),
    [lang]
  );

  const tBuffer = useCallback(
    (message) => translateMapBuffer(lang, message),
    [lang]
  );

  const formatSlot = useCallback(
    (slot, customTime) => formatDriverDeliverySlot(lang, slot, customTime),
    [lang]
  );

  const formatDeliveryDate = useCallback(
    (iso) => formatDriverDeliveryDate(lang, iso),
    [lang]
  );

  const formatWeight = useCallback(
    (weightKg) => formatDriverWeight(lang, weightKg),
    [lang]
  );

  const formatAddress = useCallback(
    (address) => formatDriverAddress(lang, address),
    [lang]
  );

  const formatNavDate = useCallback(
    (iso, isToday) => {
      if (isToday) return t("today");
      if (!iso) return "";
      try {
        const dt = new Date(`${iso}T12:00:00`);
        return dt.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch {
        return iso;
      }
    },
    [lang, t]
  );

  useEffect(() => {
    applyDriverDocumentTitle(lang);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang,
      t,
      tError,
      tBuffer,
      formatSlot,
      formatDeliveryDate,
      formatWeight,
      formatAddress,
      formatNavDate,
    }),
    [lang, setLang, toggleLang, t, tError, tBuffer, formatSlot, formatDeliveryDate, formatWeight, formatAddress, formatNavDate]
  );

  return (
    <DriverLanguageContext.Provider value={value}>{children}</DriverLanguageContext.Provider>
  );
}

export function useDriverLang() {
  const ctx = useContext(DriverLanguageContext);
  if (!ctx) {
    throw new Error("useDriverLang must be used within DriverLanguageProvider");
  }
  return ctx;
}

/** Safe for shared headers outside DA provider (returns null). */
export function useDriverLangMaybe() {
  return useContext(DriverLanguageContext);
}
